import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ClipboardCopy, ExternalLink, Globe, Plus, Star, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Checkbox,
  EmptyState,
  Field,
  Input,
  Label,
  LoadingState,
  Select,
  Textarea,
} from '@/components/ui/primitives';
import {
  ConfirmButton,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Tooltip,
} from '@/components/ui/overlays';
import { cn, copyToClipboard, formatDateTime, severityClass } from '@/lib/utils';
import {
  ISSUE_CONFIDENCES,
  ISSUE_SEVERITIES,
  ISSUE_STATUSES,
  OPEN_ISSUE_STATUSES,
  WEBSITE_CONDITIONS,
  WEBSITE_CONDITION_TOOLTIPS,
  WEBSITE_ISSUE_CATEGORY_LABELS,
} from '@shared/enums.js';

/**
 * Website and audit context for a lead.
 *
 * `readOnly` is passed for a VA: the information is genuinely useful when
 * talking to the business, but every control here calls an admin-only endpoint,
 * so rendering them would just produce 403s.
 */
export function WebsiteAuditTab({ leadId, readOnly = false }: { leadId: number; readOnly?: boolean }) {
  const queryClient = useQueryClient();
  const [addWebsiteOpen, setAddWebsiteOpen] = React.useState(false);
  const [findingOpen, setFindingOpen] = React.useState<number | null>(null);
  const [showResolved, setShowResolved] = React.useState(false);

  const audit = useQuery({ queryKey: ['audit', leadId], queryFn: () => api.auditTab(leadId) });
  const lead = useQuery({ queryKey: ['lead', leadId], queryFn: () => api.getLead(leadId) });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['audit', leadId] });
    queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
    queryClient.invalidateQueries({ queryKey: ['leads'] });
  };

  const setPrimary = useMutation({
    mutationFn: (websiteId: number) => api.setPrimaryWebsite(leadId, websiteId),
    onSuccess: () => {
      invalidate();
      toast.success('Primary website updated');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteWebsite = useMutation({
    mutationFn: (websiteId: number) => api.deleteWebsite(websiteId),
    onSuccess: () => {
      invalidate();
      toast.success('Website removed');
    },
  });

  const updateCondition = useMutation({
    mutationFn: ({ id, condition }: { id: number; condition: string }) => api.updateWebsite(id, { condition }),
    onSuccess: () => {
      invalidate();
      toast.success('Condition updated');
    },
  });

  const setNoWebsite = useMutation({
    mutationFn: (confirmed: boolean) => api.setNoWebsite(leadId, confirmed),
    onSuccess: (result) => {
      invalidate();
      toast.success(`Web presence: ${result.webPresenceStatus}`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const setIssueStatus = useMutation({
    mutationFn: ({ id, status, notes }: { id: number; status: string; notes?: string }) =>
      api.setIssueStatus(id, status, notes),
    onSuccess: () => {
      invalidate();
      toast.success('Finding updated');
    },
  });

  if (audit.isLoading) return <LoadingState />;
  const data = audit.data;
  if (!data) return null;

  const openIssues = data.issues.filter((i) => OPEN_ISSUE_STATUSES.includes(i.issueStatus));
  const resolvedIssues = data.issues.filter((i) => !OPEN_ISSUE_STATUSES.includes(i.issueStatus));
  const needsReview = openIssues.filter((i) => i.issueStatus === 'Needs Review');
  const primaryWebsite = data.websites.find((w) => w.isPrimary);

  return (
    <div className="space-y-3">
      {/* ---- Websites ---- */}
      <Card>
        <CardHeader>
          <CardTitle>Websites</CardTitle>
          <div className="flex items-center gap-2">
            {data.websites.length === 0 && (
              <label className="flex items-center gap-1.5 text-2xs text-muted-foreground">
                <Checkbox
                  checked={!!lead.data?.lead.noWebsiteConfirmed}
                  disabled={readOnly}
                  onCheckedChange={(v) => setNoWebsite.mutate(!!v)}
                />
                Confirmed no website
              </label>
            )}
            {!readOnly && <Button variant="outline" size="xs" onClick={() => setAddWebsiteOpen(true)}>
              <Plus className="h-3 w-3" />
              Add website
            </Button>}
          </div>
        </CardHeader>
        <CardBody className="space-y-2">
          {data.websites.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No websites recorded.{' '}
              {lead.data?.lead.noWebsiteConfirmed
                ? 'No official website found under the recorded policy.'
                : 'Tick “Confirmed no website” once you have checked.'}
            </p>
          ) : (
            data.websites.map((site) => (
              <div key={site.id} className="flex items-center gap-2 rounded border border-border px-2 py-1.5">
                <Globe className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <a
                  href={site.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="min-w-0 flex-1 truncate text-xs text-primary hover:underline"
                >
                  {site.normalizedDomain}
                  <ExternalLink className="ml-1 inline h-3 w-3" />
                </a>
                {site.isPrimary ? (
                  <Badge variant="primary">Primary</Badge>
                ) : (
                  <Tooltip content="Make primary">
                    <button
                      className="text-muted-foreground hover:text-primary"
                      disabled={readOnly}
                      onClick={() => setPrimary.mutate(site.id)}
                    >
                      <Star className="h-3.5 w-3.5" />
                    </button>
                  </Tooltip>
                )}
                {!site.isOfficial && <Badge>Directory/social</Badge>}
                <Tooltip content={WEBSITE_CONDITION_TOOLTIPS[site.condition]}>
                  <Select
                    value={site.condition}
                    disabled={readOnly}
                    onChange={(e) => updateCondition.mutate({ id: site.id, condition: e.target.value })}
                    className="h-6 w-44 text-2xs"
                  >
                    {WEBSITE_CONDITIONS.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </Select>
                </Tooltip>
                {!readOnly && <Button variant="outline" size="xs" onClick={() => setFindingOpen(site.id)}>
                  Record findings
                </Button>}
                {!readOnly && <ConfirmButton
                  title="Remove this website?"
                  description="Its audits and findings are removed with it."
                  confirmLabel="Remove"
                  onConfirm={() => deleteWebsite.mutate(site.id)}
                >
                  <button className="text-muted-foreground hover:text-red-300">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </ConfirmButton>}
              </div>
            ))
          )}
        </CardBody>
      </Card>

      {/* ---- Open findings ---- */}
      <Card>
        <CardHeader>
          <CardTitle>
            Open findings
            {openIssues.length > 0 && <span className="ml-1 text-muted-foreground">({openIssues.length})</span>}
          </CardTitle>
          {needsReview.length > 0 && <Badge variant="warning">{needsReview.length} need review</Badge>}
        </CardHeader>
        <CardBody>
          {openIssues.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              {data.audits.length === 0
                ? 'No audit data yet. Import findings from an external tool, or record them by hand.'
                : 'No open findings — the last audit came back clean.'}
            </p>
          ) : (
            <div className="space-y-2">
              {openIssues.map((issue) => (
                <div key={issue.id} className="rounded border border-border p-2">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge className={cn('border', severityClass(issue.severity))}>{issue.severity}</Badge>
                    <Badge variant="outline">{issue.confidence}</Badge>
                    {issue.policyVersion && <Badge variant="outline">{issue.confirmationState}</Badge>}
                    <Badge>{WEBSITE_ISSUE_CATEGORY_LABELS[issue.issueCategory]}</Badge>
                    {issue.issueStatus === 'Needs Review' && <Badge variant="warning">Needs review</Badge>}
                    <span className="ml-auto text-2xs text-muted-foreground">
                      seen {formatDateTime(issue.lastObservedAt)}
                    </span>
                  </div>
                  <p className="mt-1 text-sm font-medium">{issue.title ?? issue.issueTypeLabel}</p>
                  {issue.description && <p className="mt-0.5 text-xs text-muted-foreground">{issue.description}</p>}
                  {issue.affectedUrl && (
                    <a
                      href={issue.affectedUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="mt-1 block truncate text-2xs text-primary hover:underline"
                    >
                      {issue.affectedUrl}
                    </a>
                  )}
                  {issue.evidenceValue && issue.evidenceType !== 'none' && (
                    <div className="mt-1 rounded bg-surface-raised px-2 py-1">
                      <Label>Evidence ({issue.evidenceType})</Label>
                      <pre className="mt-0.5 overflow-x-auto whitespace-pre-wrap font-mono text-2xs">
                        {issue.evidenceValue}
                      </pre>
                    </div>
                  )}
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {(['Confirmed', 'Resolved', 'False Positive', 'Ignored'] as const).map((status) => (
                      <Button
                        key={status}
                        variant="outline"
                        size="xs"
                        disabled={readOnly}
                        onClick={() => setIssueStatus.mutate({ id: issue.id, status })}
                      >
                        {status === 'Confirmed' && <Check className="h-3 w-3" />}
                        {status}
                      </Button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {/* ---- Outreach summary ---- */}
      {openIssues.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Copyable outreach summary</CardTitle>
            <Button
              variant="outline"
              size="xs"
              onClick={async () => {
                if (await copyToClipboard(data.outreachSummary)) toast.success('Summary copied');
              }}
            >
              <ClipboardCopy className="h-3 w-3" />
              Copy
            </Button>
          </CardHeader>
          <CardBody>
            <pre className="whitespace-pre-wrap font-sans text-xs text-muted-foreground">
              {data.outreachSummary}
            </pre>
          </CardBody>
        </Card>
      )}

      {/* ---- Audit history ---- */}
      <Card>
        <CardHeader>
          <CardTitle>Audit history</CardTitle>
        </CardHeader>
        <CardBody>
          {data.audits.length === 0 ? (
            <p className="text-xs text-muted-foreground">No audits recorded.</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Audited</th>
                  <th>Source</th>
                  <th className="text-right">Findings</th>
                  <th className="text-right">Health</th>
                  <th>Worst</th>
                  <th>Summary</th>
                </tr>
              </thead>
              <tbody>
                {data.audits.map((a) => (
                  <tr key={a.id}>
                    <td className="whitespace-nowrap">{formatDateTime(a.auditedAt)}</td>
                    <td>
                      <Badge variant="outline">{a.auditSource}</Badge>
                    </td>
                    <td className="text-right tabular-nums">{a.issueCount}</td>
                    <td className="text-right tabular-nums">{a.overallHealthScore ?? '—'}</td>
                    <td>
                      {a.overallSeverity ? (
                        <Badge className={cn('border', severityClass(a.overallSeverity))}>{a.overallSeverity}</Badge>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="max-w-md truncate text-muted-foreground">{a.summary ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      {/* ---- Resolved ---- */}
      {resolvedIssues.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Resolved and dismissed ({resolvedIssues.length})</CardTitle>
            <Button variant="ghost" size="xs" onClick={() => setShowResolved((v) => !v)}>
              {showResolved ? 'Hide' : 'Show'}
            </Button>
          </CardHeader>
          {showResolved && (
            <CardBody className="space-y-1.5">
              {resolvedIssues.map((issue) => (
                <div key={issue.id} className="flex items-center gap-2 text-xs">
                  <Badge variant="outline">{issue.issueStatus}</Badge>
                  <span className="min-w-0 flex-1 truncate text-muted-foreground">
                    {issue.title ?? issue.issueTypeLabel}
                  </span>
                  <span className="shrink-0 text-2xs text-muted-foreground">
                    {issue.resolvedAt ? formatDateTime(issue.resolvedAt) : ''}
                  </span>
                  <Button
                    variant="ghost"
                    size="xs"
                    disabled={readOnly}
                    onClick={() => setIssueStatus.mutate({ id: issue.id, status: 'Open' })}
                  >
                    Reopen
                  </Button>
                </div>
              ))}
            </CardBody>
          )}
        </Card>
      )}

      <AddWebsiteDialog open={addWebsiteOpen} onOpenChange={setAddWebsiteOpen} leadId={leadId} onDone={invalidate} />
      <ManualFindingDialog
        websiteId={findingOpen}
        onClose={() => setFindingOpen(null)}
        leadId={leadId}
        onDone={invalidate}
      />
    </div>
  );
}

function AddWebsiteDialog({
  open,
  onOpenChange,
  leadId,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leadId: number;
  onDone: () => void;
}) {
  const [url, setUrl] = React.useState('');
  const [condition, setCondition] = React.useState('Unknown');
  const [isPrimary, setIsPrimary] = React.useState(true);

  const add = useMutation({
    mutationFn: () => api.addWebsite(leadId, { url: url.trim(), condition, isPrimary }),
    onSuccess: () => {
      onDone();
      onOpenChange(false);
      setUrl('');
      toast.success('Website added');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Add a website</DialogTitle>
          <DialogDescription>
            Social and directory URLs are recognised automatically and are not treated as an official website.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <Field label="URL">
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="example.com"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && url.trim()) add.mutate();
              }}
            />
          </Field>
          <Field label="Condition">
            <Select value={condition} onChange={(e) => setCondition(e.target.value)}>
              {WEBSITE_CONDITIONS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </Field>
          <label className="flex items-center gap-2 text-xs">
            <Checkbox checked={isPrimary} onCheckedChange={(v) => setIsPrimary(!!v)} />
            Make this the primary website
          </label>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button size="sm" disabled={!url.trim()} loading={add.isPending} onClick={() => add.mutate()}>
            Add website
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface DraftFinding {
  issueTypeCode: string;
  title: string;
  description: string;
  severity: string;
  confidence: string;
  affectedUrl: string;
  evidenceType: string;
  evidenceValue: string;
}

function ManualFindingDialog({
  websiteId,
  onClose,
  leadId,
  onDone,
}: {
  websiteId: number | null;
  onClose: () => void;
  leadId: number;
  onDone: () => void;
}) {
  const issueTypes = useQuery({ queryKey: ['issue-types'], queryFn: api.issueTypes, enabled: websiteId !== null });
  const [summary, setSummary] = React.useState('');
  const [drafts, setDrafts] = React.useState<DraftFinding[]>([]);

  React.useEffect(() => {
    if (websiteId !== null) {
      setSummary('');
      setDrafts([]);
    }
  }, [websiteId]);

  const grouped = React.useMemo(() => {
    const map = new Map<string, typeof issueTypes.data>();
    for (const type of issueTypes.data ?? []) {
      const list = map.get(type.category) ?? [];
      list.push(type);
      map.set(type.category, list as never);
    }
    return map;
  }, [issueTypes.data]);

  const addDraft = (code: string) => {
    const type = issueTypes.data?.find((t) => t.code === code);
    if (!type) return;
    setDrafts((d) => [
      ...d,
      {
        issueTypeCode: code,
        title: type.label,
        description: '',
        severity: type.defaultSeverity,
        confidence: type.defaultConfidence,
        affectedUrl: '',
        evidenceType: 'none',
        evidenceValue: '',
      },
    ]);
  };

  const save = useMutation({
    mutationFn: () =>
      api.recordFinding(leadId, {
        websiteId,
        summary: summary.trim() || null,
        issues: drafts.map((d) => ({
          issueTypeCode: d.issueTypeCode,
          title: d.title.trim() || null,
          description: d.description.trim() || null,
          severity: d.severity,
          confidence: d.confidence,
          affectedUrl: d.affectedUrl.trim() || null,
          evidenceType: d.evidenceType,
          evidenceValue: d.evidenceValue.trim() || null,
        })),
      }),
    onSuccess: (result) => {
      onDone();
      onClose();
      if (result.warnings?.length) {
        result.warnings.forEach((w) => toast.warning(w));
      }
      toast.success('Findings recorded');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Dialog open={websiteId !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Record website findings</DialogTitle>
          <DialogDescription>
            Creates a manual audit. Existing audits are never changed — history is append-only.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <Field label="Audit summary (optional)">
            <Input value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="What you checked" />
          </Field>

          <div>
            <Label>Add a finding</Label>
            <Select
              value=""
              onChange={(e) => {
                if (e.target.value) addDraft(e.target.value);
              }}
              className="mt-0.5"
            >
              <option value="">Choose an issue type…</option>
              {[...grouped.entries()].map(([category, types]) => (
                <optgroup
                  key={category}
                  label={WEBSITE_ISSUE_CATEGORY_LABELS[category as keyof typeof WEBSITE_ISSUE_CATEGORY_LABELS]}
                >
                  {types?.map((t) => (
                    <option key={t.code} value={t.code}>
                      {t.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </Select>
          </div>

          {drafts.length === 0 ? (
            <EmptyState title="No findings added yet" description="Pick an issue type above to start." />
          ) : (
            <div className="space-y-2">
              {drafts.map((draft, index) => (
                <Card key={index}>
                  <CardBody className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{draft.issueTypeCode}</Badge>
                      <button
                        className="ml-auto text-muted-foreground hover:text-red-300"
                        onClick={() => setDrafts((d) => d.filter((_, i) => i !== index))}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Field label="Title">
                        <Input
                          value={draft.title}
                          onChange={(e) =>
                            setDrafts((d) => d.map((x, i) => (i === index ? { ...x, title: e.target.value } : x)))
                          }
                        />
                      </Field>
                      <Field label="Affected URL">
                        <Input
                          value={draft.affectedUrl}
                          onChange={(e) =>
                            setDrafts((d) =>
                              d.map((x, i) => (i === index ? { ...x, affectedUrl: e.target.value } : x)),
                            )
                          }
                        />
                      </Field>
                      <Field label="Severity">
                        <Select
                          value={draft.severity}
                          onChange={(e) =>
                            setDrafts((d) => d.map((x, i) => (i === index ? { ...x, severity: e.target.value } : x)))
                          }
                        >
                          {ISSUE_SEVERITIES.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </Select>
                      </Field>
                      <Field label="Confidence">
                        <Select
                          value={draft.confidence}
                          onChange={(e) =>
                            setDrafts((d) =>
                              d.map((x, i) => (i === index ? { ...x, confidence: e.target.value } : x)),
                            )
                          }
                        >
                          {ISSUE_CONFIDENCES.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </Select>
                      </Field>
                    </div>
                    <Field label="What you observed">
                      <Textarea
                        rows={2}
                        value={draft.description}
                        onChange={(e) =>
                          setDrafts((d) =>
                            d.map((x, i) => (i === index ? { ...x, description: e.target.value } : x)),
                          )
                        }
                      />
                    </Field>
                    <div className="grid grid-cols-[8rem_1fr] gap-2">
                      <Field label="Evidence type">
                        <Select
                          value={draft.evidenceType}
                          onChange={(e) =>
                            setDrafts((d) =>
                              d.map((x, i) => (i === index ? { ...x, evidenceType: e.target.value } : x)),
                            )
                          }
                        >
                          {['none', 'url', 'http_status', 'header', 'html_snippet', 'screenshot_path', 'text'].map(
                            (t) => (
                              <option key={t} value={t}>
                                {t}
                              </option>
                            ),
                          )}
                        </Select>
                      </Field>
                      <Field label="Evidence">
                        <Input
                          value={draft.evidenceValue}
                          onChange={(e) =>
                            setDrafts((d) =>
                              d.map((x, i) => (i === index ? { ...x, evidenceValue: e.target.value } : x)),
                            )
                          }
                          disabled={draft.evidenceType === 'none'}
                        />
                      </Field>
                    </div>
                  </CardBody>
                </Card>
              ))}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" disabled={drafts.length === 0} loading={save.isPending} onClick={() => save.mutate()}>
            Record {drafts.length} finding{drafts.length === 1 ? '' : 's'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
